// Port of ios/Sources/ViewModels/ReactionsDerivation.swift.
//
// Pure grouping of raw reaction rows into the chips a message renders — count per emoji, whether
// the current user is among them, stable order. No DOM dependency, mirroring Android's
// `ReactionsDerivation` decision for decision so the two clients can never disagree about what a
// chip says.
import type { ReactionRecord, UserRecord } from './records';
import { userDisplayName } from './channelListDerivation';

export interface ReactionChip {
  /** Also the chip's identity (Swift `id`). */
  emojiName: string;
  count: number;
  mine: boolean;
  /**
   * Who reacted and when, one per line — the desktop's hover tooltip on the chip (the
   * webapp's own "X and Y reacted with :+1:"). Empty when the caller had no names to give.
   */
  tooltip: string;
}

/** One person's reaction inside a group: who, and when (epoch millis; 0 = not known). */
export interface ReactionDetailEntry {
  userId: string;
  name: string;
  createAt: number;
}

/** One emoji's who-reacted group. Groups keep the chips' order: first reaction first. */
export interface ReactionDetailGroup {
  emojiName: string;
  entries: ReactionDetailEntry[];
}

type ReactionRow = Pick<ReactionRecord, 'userId' | 'emojiName'> & Partial<Pick<ReactionRecord, 'postId' | 'createAt'>>;

/**
 * Chips ordered by FIRST APPEARANCE in the row list — the order reactions arrived — never by
 * count: a chip that jumps position every time someone taps it is unreadable.
 */
function chips(records: readonly ReactionRow[], currentUserId: string): ReactionChip[] {
  const order: string[] = [];
  const counts = new Map<string, number>();
  const mine = new Set<string>();
  for (const record of records) {
    const prior = counts.get(record.emojiName);
    if (prior === undefined) order.push(record.emojiName);
    counts.set(record.emojiName, (prior ?? 0) + 1);
    if (record.userId === currentUserId) mine.add(record.emojiName);
  }
  return order.map((name) => ({ emojiName: name, count: counts.get(name) ?? 0, mine: mine.has(name), tooltip: '' }));
}

/** Chips for every post on the page, keyed by post id. */
function chipsByPost(
  recordsByPost: ReadonlyMap<string, readonly ReactionRecord[]>,
  currentUserId: string,
): Map<string, ReactionChip[]> {
  const out = new Map<string, ReactionChip[]>();
  for (const [postId, records] of recordsByPost) out.set(postId, chips(records, currentUserId));
  return out;
}

export interface ChipsWithTooltipsArgs {
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  youLabel: string;
  unknownLabel: string;
  /**
   * The clock printed beside each person, or null for none. The Swift default is
   * `ReactionTime.label(epochMillis:)` against the wall clock; here the caller builds it with
   * its own `now` (`(ms) => ReactionTime.label(ms, { now })`), because a core module never reads
   * the clock behind the caller's back.
   */
  timeLabel: (epochMillis: number) => string | null;
}

/**
 * Chips with their who-and-when tooltip filled. "I cannot see who reacted and when, this is
 * a must" (user, 2026-10-07): the sheet existed but hid behind a long-press, which a
 * desktop pointer never does. One line per person, in reaction order, under the shortcode.
 *
 * (Swift overloads `chipsByPost`; TypeScript cannot, hence the separate name.)
 */
function chipsByPostWithTooltips(
  recordsByPost: ReadonlyMap<string, readonly ReactionRecord[]>,
  args: ChipsWithTooltipsArgs,
): Map<string, ReactionChip[]> {
  const out = new Map<string, ReactionChip[]>();
  for (const [postId, records] of recordsByPost) {
    const groups = new Map<string, ReactionDetailGroup>();
    for (const group of details(records, args)) groups.set(group.emojiName, group);
    out.set(
      postId,
      chips(records, args.currentUserId).map((chip) => ({
        ...chip,
        tooltip: tooltip(groups.get(chip.emojiName) ?? null, args.timeLabel),
      })),
    );
  }
  return out;
}

function tooltip(group: ReactionDetailGroup | null, timeLabel: (epochMillis: number) => string | null): string {
  if (!group) return '';
  const lines = group.entries.map((entry) => {
    const time = timeLabel(entry.createAt);
    return time != null ? `${entry.name} · ${time}` : entry.name;
  });
  return [`:${group.emojiName}:`, ...lines].join('\n');
}

/**
 * Every user id that reacted on the page — the caller's user fetch needs them, because a
 * reactor is often not an author of anything on screen.
 */
function reactorIds(recordsByPost: ReadonlyMap<string, readonly ReactionRecord[]>): Set<string> {
  const ids = new Set<string>();
  for (const records of recordsByPost.values()) for (const r of records) ids.add(r.userId);
  return ids;
}

/** The names of a group's people, in order (Swift `DetailGroup.names`). */
function names(group: ReactionDetailGroup): string[] {
  return group.entries.map((e) => e.name);
}

/**
 * "Tú" renders IN PLACE by reaction time — who reacted first is real information. A
 * reactor with no synced record still counts as `unknownLabel`, so the sheet always
 * matches the chip's number.
 */
function details(
  reactions: readonly ReactionRecord[],
  args: Pick<ChipsWithTooltipsArgs, 'usersById' | 'currentUserId' | 'youLabel' | 'unknownLabel'>,
): ReactionDetailGroup[] {
  const firstSeen = new Map<string, number>();
  const grouped = new Map<string, ReactionRecord[]>();
  for (const reaction of reactions) {
    const existing = firstSeen.get(reaction.emojiName);
    if (existing === undefined || reaction.createAt < existing) firstSeen.set(reaction.emojiName, reaction.createAt);
    const bucket = grouped.get(reaction.emojiName);
    if (bucket) bucket.push(reaction);
    else grouped.set(reaction.emojiName, [reaction]);
  }
  return [...grouped.entries()]
    .sort(([a], [b]) => {
      const fa = firstSeen.get(a) ?? Number.MAX_SAFE_INTEGER;
      const fb = firstSeen.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (fa !== fb) return fa - fb;
      return a < b ? -1 : a > b ? 1 : 0;
    })
    .map(([emojiName, rows]) => ({
      emojiName,
      entries: [...rows]
        .sort((x, y) => x.createAt - y.createAt)
        .map((row) => {
          let name: string;
          if (row.userId === args.currentUserId) {
            name = args.youLabel;
          } else {
            const user = args.usersById.get(row.userId);
            name = user ? userDisplayName(user) : args.unknownLabel;
          }
          return { userId: row.userId, name, createAt: row.createAt };
        }),
    }));
}

export const ReactionsDerivation = {
  chips,
  chipsByPost,
  chipsByPostWithTooltips,
  tooltip,
  reactorIds,
  names,
  details,
} as const;
